"use client";

import { Button } from "@/components/shadcn/button";
import { Card, CardContent } from "@/components/shadcn/card";
import { Spinner } from "@/components/shadcn/spinner";
import { useSession } from "@/lib/auth/client";
import { getFormattedViewCounts } from "@/lib/utils";
import axios from "axios";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import DOMPurify from "isomorphic-dompurify";
import { ThumbsUp } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import ShareButtons from "./share-buttons";
import { NewsArticlePayload } from "./types";

interface NewsArticleContentProps {
    newsArticle: NewsArticlePayload;
    shareUrl: string;
}

function getReadingMinutes(htmlContent: string | null | undefined) {
    const wordCount = (htmlContent ?? "")
        .replace(/<[^>]*>/g, " ")
        .split(/\s+/)
        .filter(Boolean).length;
    return Math.max(1, Math.round(wordCount / 200));
}

export default function NewsArticleContent({
    newsArticle,
    shareUrl,
}: NewsArticleContentProps) {
    const t = useTranslations("news");
    const { data: session } = useSession();

    const [likesCount, setLikesCount] = useState(
        newsArticle?._count.NewsLikes ?? 0,
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isLiked, setIsLiked] = useState(false);

    useEffect(() => {
        if (newsArticle && session?.user) {
            const fetchData = async () => {
                const { data: likesData } = await axios.get(
                    `/api/news-articles/like?id=${newsArticle.Id}`,
                );
                if (likesData) {
                    setIsLiked(likesData.isLiked);
                }
            };
            fetchData();
        }
    }, [newsArticle, session?.user, setIsLiked]);

    const handleLike = async () => {
        if (!session?.user) return;

        const data = { Id: newsArticle?.Id };
        setIsLoading(true);
        try {
            const { data: responseData } = await axios.post(
                "/api/news-articles/like",
                data,
                {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                },
            );
            if (responseData.success) {
                setIsLiked(responseData.isLiked);
                setLikesCount(likesCount + (responseData.isLiked ? 1 : -1));
            }
        } catch (error) {
            console.error("Error like news article: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    function sanitizedHTMLContent() {
        return {
            __html: DOMPurify.sanitize(newsArticle?.Content ?? ""),
        };
    }

    return (
        <div className="flex flex-col gap-8 lg:flex-row">
            <article className="min-w-0 flex-1">
                <div className="flex flex-col gap-4">
                    <h1 className="text-3xl font-medium">
                        {newsArticle?.Title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                            {newsArticle?.AdminUsers?.fullname && (
                                <>
                                    <span className="text-foreground font-medium">
                                        {newsArticle.AdminUsers.fullname}
                                    </span>
                                    <span>·</span>
                                </>
                            )}
                            <span>
                                {format(
                                    newsArticle?.NaviDate || new Date(),
                                    "d MMMM yyyy",
                                    { locale: tr },
                                )}
                            </span>
                            <span>·</span>
                            <span>
                                {t("readingTime", {
                                    minutes: getReadingMinutes(
                                        newsArticle?.Content,
                                    ),
                                })}
                            </span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            onClick={handleLike}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Spinner />
                            ) : (
                                <ThumbsUp
                                    fill={isLiked ? "currentColor" : "none"}
                                />
                            )}
                            {t("like")}
                            <span className="text-muted-foreground font-normal">
                                ({getFormattedViewCounts(likesCount)})
                            </span>
                        </Button>
                    </div>
                    <div
                        className="news-article prose w-full max-w-none"
                        dangerouslySetInnerHTML={sanitizedHTMLContent()}
                    ></div>
                    {newsArticle?.CiteText && (
                        <blockquote className="border-primary border-l-2 pl-6 text-2xl leading-8 font-medium">
                            {newsArticle.CiteText}
                        </blockquote>
                    )}
                </div>
            </article>
            <aside className="w-full shrink-0 lg:w-72">
                <Card className="lg:sticky lg:top-24">
                    <CardContent>
                        <ShareButtons shareUrl={shareUrl} />
                    </CardContent>
                </Card>
            </aside>
        </div>
    );
}
